/* eslint-disable max-classes-per-file */
import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { A365Named } from '../base/a365Named';
import { ModelConstants } from '../model-constants';
import { IViolation } from '../base/annotation';
import { ActionConstants } from '../../constants/action-constants';
import { getGeneralNamePatterns } from '../../shared/utils/validation.util';
import { SparkConfiguration } from '../sparkconfiguration/sparkconfiguration';

export type SparkJobDefinitionLanguage = 'scala' | 'python' | 'csharp';

const SPARK_JOB_DEFINITION_DEFAULT_LANGUAGE = 'scala';

@Metamodel.model({
    name: 'Spark Job Properties'
})
export class SparkJobProperties {
    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    public name: string;

    @Metamodel.attribute({
        display: shellStringResources.File
    })
    public file: string;

    @Metamodel.attribute({
        display: shellStringResources.ClassName
    })
    public className: string;

    @Metamodel.attribute({
        display: shellStringResources.Configuration
    })
    public conf?: { [key: string]: string };

    @Metamodel.attribute({
        display: shellStringResources.Arguments
    })
    public args?: string[];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesJars
    })
    public jars?: string[];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesPyFiles
    })
    public pyFiles?: string[];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesFiles
    })
    public files?: string[];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesArchives
    })
    public archives?: string[];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverMemory
    })
    public driverMemory: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverCores
    })
    public driverCores: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorMemory
    })
    public executorMemory: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorCores
    })
    public executorCores: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesNumExecutors
    })
    public numExecutors: number;
}

@Metamodel.model({
    name: ModelConstants.sparkJobDefinition,
    display: [shellStringResources.SynapseSparkJobDefinition, shellStringResources.SynapseSparkJobDefinitions],
    reusable: true,
    referable: true,
    validator: SparkJobDefinition.validate,
    constraints: {
        name: {
            required: true,
            patterns: getGeneralNamePatterns(shellStringResources.SynapseSparkJobDefinition),
            uniqueModelName: true
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.sparkJobDefinitionsWrite
        },
        deleteResource: {
            action: ActionConstants.sparkJobDefinitionsDelete
        }
    }
})
export class SparkJobDefinition extends A365Named {
    public readonly useShellFolder: boolean = true;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionTargetSparkCompute
    })
    public targetSparkCompute: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionRequiredSparkVersion
    })
    public requiredSparkVersion: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionLanguage
    })
    public language: SparkJobDefinitionLanguage;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionJobProperties
    })
    public jobProperties: SparkJobProperties;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionScanFolder
    })
    public scanFolder: boolean;

    public violations: string[];
    @Metamodel.attribute({
        display: shellStringResources.ApacheSparkConfiguration,
        type: SparkConfiguration.MODEL,
        reference: true
    })
    public targetSparkConfiguration?: SparkConfiguration;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.language = SPARK_JOB_DEFINITION_DEFAULT_LANGUAGE;
        this.jobProperties = new SparkJobProperties();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validate(owner: any): IViolation[] | undefined {
        const named = (owner && owner.named) as SparkJobDefinition;
        if (named && named.violations) {
            return named.violations.map((violation) => ({
                context: owner,
                message: () => violation
            }));
        }

        return undefined;
    }
}
